// Tracks whether the signed-in user has 2FA enabled.
//
// Per the CLAUDE.md security posture ("MFA required for every user
// login, no exceptions"), MFA is enforced: the MfaGate in App.tsx
// routes any employee who hasn't enrolled to /security until they do.
// The status normally arrives ON the session payload (/api/session
// includes mfa_enabled since Aug 2026), so the boot path costs zero
// extra requests — the old serial second round trip
// (session → mfa-status → first render) was measurable lag on every
// cold load. The /api/me/mfa-status fetch remains for two cases: the
// SecurityPage's refresh() right after enrollment (so the gate
// releases without a page reload), and a fallback when the session
// payload predates the field (cached pre-upgrade Worker response).
//
// Mounted ABOVE the router (inside SessionProvider) so the value
// persists across navigations — the gate doesn't re-fetch per page.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

import { api } from "./api";
import { useSession } from "./session";

interface MfaContextValue {
  // null = unknown / still loading. true / false once known.
  enabled: boolean | null;
  refresh: () => Promise<void>;
}

const MfaContext = createContext<MfaContextValue | null>(null);

export function MfaProvider({ children }: { children: ReactNode }) {
  const { status, realUser } = useSession();
  const [enabled, setEnabled] = useState<boolean | null>(null);

  const refresh = useCallback(async () => {
    try {
      const s = await api.getMfaStatus();
      setEnabled(s.enabled);
    } catch {
      // Fail-open: never trap the whole app behind the gate because of a
      // transient status-fetch error. Enforcement still applies whenever
      // we positively learn 2FA is off.
      //
      // This client gate is now UX only — the real enforcement lives at
      // the query layer (workers/api requireAuth returns 403
      // mfa_enrollment_required for un-enrolled employees on every
      // non-exempt route). So an un-enrolled user who slips past this
      // fail-open still cannot read or write any gated data; the worst
      // case is a confusing wall of 403s instead of a clean redirect to
      // /security. Keeping it fail-open here avoids a transient blip
      // bouncing an already-enrolled user to /security needlessly.
      setEnabled(true);
    }
  }, []);

  // The session payload's flag, when present. Read from the REAL
  // signed-in human (realUser === user when not impersonating) — the
  // server's MFA enforcement gate checks the real human too
  // (require-auth.ts: "starting an impersonation already requires an
  // authenticated, hence enrolled, admin"). Reading the EFFECTIVE
  // user's flag here bounced an enrolled admin to /security for the
  // whole impersonation whenever they viewed-as a not-yet-enrolled
  // new hire (pre-promote audit, 2026-08-14) — the exact flow
  // view-as exists for.
  const sessionMfa = realUser?.mfa_enabled;

  useEffect(() => {
    if (status === "signed_in") {
      if (typeof sessionMfa === "boolean") {
        // Zero-cost path: the session response already told us.
        setEnabled(sessionMfa);
      } else {
        // Session payload predates the mfa_enabled field — fall back to
        // the dedicated endpoint (one extra request, exactly the old
        // behavior).
        void refresh();
      }
    } else {
      // Signed out / loading — reset so the next sign-in re-checks.
      setEnabled(null);
    }
  }, [status, sessionMfa, refresh]);

  return (
    <MfaContext.Provider value={{ enabled, refresh }}>
      {children}
    </MfaContext.Provider>
  );
}

export function useMfa(): MfaContextValue {
  const ctx = useContext(MfaContext);
  if (!ctx) {
    throw new Error("useMfa must be used inside <MfaProvider>.");
  }
  return ctx;
}
